import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LogOut,
  KeyRound,
  PenLine,
  Settings,
  Mail,
  Sun,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslations } from '@/i18n/useTranslations';
import { useTheme } from '@/components/theme-provider';
import { useNavigation } from '@/hooks/use-navigation';
import { useLogout } from '@/hooks/use-logout';
import { useAuthStore } from '@/stores/auth-store';
import { useSidebarStore } from '@/stores/sidebar-store';
import { useMessageStore } from '@/stores/message-store';
import { useUiModeStore } from '@/stores/ui-mode-store';
import { useAiChatStore } from '@/stores/ai-chat-store';
import { useShowTryGuidedButton } from '@/hooks/use-guided-ui-mode';
import { useFooterConfig } from '@/hooks/use-footer-config';
import { UserRole } from '@comp-sys/shared';
import { stripLocale } from '@/lib/navigation-config';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { MobileSidebar } from '@/components/layout/mobile-sidebar';
import { BrandLogo } from '@/components/shared/brand-logo';
import { ConfigurableFooter } from '@/components/shared/configurable-footer';
import { ProfileReviewAlert } from '@/components/shared/profile-review-alert';
import { AiChatPanel } from '@/components/ai/ai-chat-panel';
import { AiFloatingButton } from '@/components/ai/ai-floating-button';
import { RoleSwitcherMenuItems } from '@/components/auth/role-switcher';
import { ChangePasswordDialog } from '@/components/users/change-password-dialog';
import { ChangeNameDialog } from '@/components/users/change-name-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Neon 家族 shell（cyber 主题）——「终端 HUD」。
 *
 * 与 Glass 基线共享同一份 shell 数据（导航/用户/footer/AI），但空间语法截然不同：
 * - 左侧「命令列」导航：等宽字体 + 热键徽标 `[N]` + 激活项 `▸` 光标，而非图标+标签列表
 * - 顶部「状态条」：实时时钟 + 路径式面包屑 `~/path` + 角色标签，取代普通顶栏
 * - 内容「视口」：角括号 + `[ MAIN ]` 标签 tab 包裹的 HUD 面板
 * - 启动式页面过渡（淡入+上浮），而非纯透明度
 *
 * 这就是「换应用」——结构、导航范式、内容几何全变，而非仅换色/换圆角。
 * 子组件各自读取所需 hook，避免 prop drilling。
 */
export function NeonShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;
  const { isCollapsed } = useSidebarStore();
  const user = useAuthStore((state) => state.user);
  const { config: footerConfig } = useFooterConfig();
  const aiIsOpen = useAiChatStore((s) => s.isOpen);
  const aiIsFloating = useAiChatStore((s) => s.isFloating);
  const isSystemAdmin = user?.activeRole === 'SYSTEM_ADMIN';

  const prefersReducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  return (
    <div className="min-h-screen">
      <CommandRail pathname={pathname} isCollapsed={isCollapsed} />

      {/* 主区 */}
      <div
        className={cn(
          'transition-[margin] duration-300 flex items-start min-h-screen',
          isCollapsed ? 'md:ml-16' : 'md:ml-60',
        )}
      >
        <div className="flex-1 min-w-0 flex flex-col min-h-screen">
          <StatusStrip pathname={pathname} />

          <main id="main-content" className="flex-1 p-3 md:p-5">
            <ProfileReviewAlert />
            <NeonViewport>
              <AnimatePresence>
                <motion.div
                  key={pathname}
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={prefersReducedMotion ? undefined : { opacity: 0, position: 'absolute' }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                  {children}
                </motion.div>
              </AnimatePresence>
            </NeonViewport>
          </main>

          <ConfigurableFooter variant="dashboard" config={footerConfig} />
        </div>

        {isSystemAdmin && aiIsOpen && !aiIsFloating && <AiChatPanel mode="fixed" />}
      </div>
      {isSystemAdmin && aiIsFloating && <AiChatPanel mode="floating" />}
      {isSystemAdmin && !aiIsOpen && <AiFloatingButton />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 左侧命令列                                                          */
/* ------------------------------------------------------------------ */

function CommandRail({ pathname, isCollapsed }: { pathname: string; isCollapsed: boolean }) {
  const { setCollapsed } = useSidebarStore();
  const { navItems } = useNavigation();
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const { logout, isPending } = useLogout();
  const t = useTranslations('nav');
  const tAuth = useTranslations('auth');
  const tUser = useTranslations('user');
  const tUsers = useTranslations('users');

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changeNameOpen, setChangeNameOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isItemActive = (href: string) =>
    pathname === href || (href !== '/dashboard' && pathname.startsWith(href + '/'));

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen flex flex-col font-mono',
        'bg-background/95 backdrop-blur-xl border-r border-primary/25',
        'transition-[width] duration-300',
        isCollapsed ? 'w-16' : 'w-60',
        'hidden md:flex',
      )}
    >
      {/* 品牌 + 折叠 */}
      <div className="flex h-14 items-center gap-2 px-3 border-b border-primary/25">
        <BrandLogo variant="auto" size="sm" collapsed={isCollapsed} linkTo="/" />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!isCollapsed)}
          className="ml-auto hidden md:flex h-7 w-7 text-primary/70 hover:text-primary"
          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
        >
          {isCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </Button>
      </div>

      {/* 命令列表 */}
      <nav className="flex-1 overflow-auto py-2 text-[13px]">
        {navItems.map((item, idx) => {
          const active = isItemActive(item.href);
          const hotkey = idx + 1 <= 9 ? String(idx + 1) : null;
          const Icon = item.icon;
          const hasChildren = !!item.children?.length;

          return (
            <div key={item.href}>
              <Link
                to={item.href}
                title={t(item.labelKey)}
                className={cn(
                  'group relative flex items-center gap-2 px-3 py-2 transition-colors',
                  active
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-primary/5',
                )}
              >
                {active && (
                  <span className="absolute left-0 top-0 h-full w-0.5 bg-primary shadow-[0_0_8px_var(--primary)]" />
                )}
                <span className={cn('w-3 flex-shrink-0 text-primary', !active && 'opacity-0')}>
                  ▸
                </span>
                <Icon className="h-4 w-4 flex-shrink-0" />
                {!isCollapsed && <span className="flex-1 truncate tracking-wide">{t(item.labelKey)}</span>}
                {!isCollapsed && hotkey && (
                  <kbd className="text-[10px] px-1 py-0.5 rounded border border-primary/30 text-primary/70">
                    {hotkey}
                  </kbd>
                )}
                {isCollapsed && hotkey && (
                  <kbd className="absolute top-0.5 right-0.5 text-[9px] px-0.5 rounded border border-primary/30 text-primary/70">
                    {hotkey}
                  </kbd>
                )}
              </Link>

              {/* 子命令（展开时） */}
              {!isCollapsed &&
                hasChildren &&
                item.children!.map((child) => {
                  const childActive = isItemActive(child.href);
                  const ChildIcon = child.icon;
                  return (
                    <Link
                      key={child.href}
                      to={child.href}
                      className={cn(
                        'flex items-center gap-2 pl-9 pr-3 py-1.5 text-[12px] transition-colors',
                        childActive ? 'text-primary bg-primary/5' : 'text-muted-foreground/80 hover:text-foreground',
                      )}
                    >
                      <span className="text-primary/40">└─</span>
                      {ChildIcon && <ChildIcon className="h-3.5 w-3.5" />}
                      <span className="truncate">{t(child.labelKey)}</span>
                    </Link>
                  );
                })}
            </div>
          );
        })}
      </nav>

      {/* 用户 prompt */}
      <div className="border-t border-primary/25 p-2">
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger
            className={cn(
              'flex items-center gap-2 w-full rounded px-2 py-2 text-left',
              'hover:bg-primary/10 transition-colors cursor-pointer',
              isCollapsed && 'justify-center',
            )}
          >
            <Avatar className="h-7 w-7 flex-shrink-0 rounded-none border border-primary/40">
              <AvatarImage src="/avatar.png" />
              <AvatarFallback className="rounded-none bg-primary/10 text-primary text-[11px]">
                {user?.displayName?.[0]?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            {!isCollapsed && (
              <div className="flex-1 min-w-0 text-[11px] leading-tight">
                <div className="text-primary truncate">
                  {user?.displayName || user?.email || 'user'}
                  <span className="text-muted-foreground">@</span>
                  <span className="text-primary/70 lowercase">{user?.activeRole?.toLowerCase()}</span>
                </div>
                <div className="text-muted-foreground/60 truncate">~$</div>
              </div>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-48 font-sans">
            <RoleSwitcherMenuItems onSwitchSuccess={() => setMenuOpen(false)} />
            <DropdownMenuItem onClick={() => setChangeNameOpen(true)}>
              <PenLine className="h-4 w-4" />
              {tUsers('changeName')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
              <KeyRound className="h-4 w-4" />
              {tUsers('changePassword')}
            </DropdownMenuItem>
            {(user?.activeRole === UserRole.SYSTEM_ADMIN || user?.activeRole === UserRole.SUPER_ADMIN) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                  <Settings className="h-4 w-4" />
                  {tAuth('logout.settings')}
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} disabled={isPending} className="text-error-text">
              <LogOut className="h-4 w-4" />
              {tAuth('logout.title')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
      <ChangeNameDialog open={changeNameOpen} onOpenChange={setChangeNameOpen} />
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/* 顶部状态条                                                          */
/* ------------------------------------------------------------------ */

function StatusStrip({ pathname }: { pathname: string }) {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const unreadCount = useMessageStore((s) => s.unreadCount);
  const tCommon = useTranslations('common');
  const tUser = useTranslations('user');
  // 引导式回切:资格角色在经典模式下可见,点此回到引导式首页。
  const setUiMode = useUiModeStore((s) => s.setUiMode);
  const guidedEligible = useShowTryGuidedButton();
  const tGuided = useTranslations('guided');
  const path = stripLocale(pathname);

  return (
    <header className="sticky top-0 z-30 h-12 border-b border-primary/25 bg-background/90 backdrop-blur-xl font-mono text-[12px]">
      <div className="flex h-full items-center gap-3 px-3 md:px-4">
        <div className="md:hidden">
          <MobileSidebar />
        </div>

        <span className="hidden sm:inline text-primary">◆</span>
        <span className="hidden md:inline text-muted-foreground truncate">
          <span className="text-primary/60">~</span>
          {path || '/'}
        </span>

        <span className="flex-1" />

        <span className="hidden lg:inline px-2 py-0.5 rounded border border-primary/30 text-primary/80 tracking-wide">
          {user ? tUser(`role.${user.activeRole}`) : ''}
        </span>

        <HudClock />

        {guidedEligible && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-primary/70 hover:text-primary font-mono text-[12px]"
            onClick={() => setUiMode('guided')}
            aria-label={tGuided('switchGuided')}
            title={tGuided('switchGuided')}
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span className="ml-1 hidden lg:inline">{tGuided('switchGuided')}</span>
          </Button>
        )}

        <LanguageSwitcher />

        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-primary/70 hover:text-primary"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          aria-label={tCommon('toggleTheme')}
        >
          <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="relative h-7 w-7 text-primary/70 hover:text-primary"
          onClick={() => navigate('/dashboard/messages')}
          aria-label={tCommon('openMessages')}
        >
          <Mail className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-bold px-1">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </div>
    </header>
  );
}

function HudClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    <span className="hidden sm:inline tabular-nums text-primary/90 tracking-widest">
      {pad(now.getHours())}:{pad(now.getMinutes())}:{pad(now.getSeconds())}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* 内容视口（角括号 HUD 面板）                                         */
/* ------------------------------------------------------------------ */

function NeonViewport({ children }: { children: ReactNode }) {
  return (
    <div className="relative">
      {/* [ MAIN ] 标签 tab */}
      <div className="absolute -top-px left-3 z-10 px-2 py-0.5 text-[10px] font-mono tracking-[0.2em] text-primary bg-background border border-b-0 border-primary/40 rounded-t-md">
        MAIN
      </div>
      {/* 四角括号 */}
      <span className="pointer-events-none absolute -top-px -left-px h-3 w-3 border-t-2 border-l-2 border-primary" />
      <span className="pointer-events-none absolute -top-px -right-px h-3 w-3 border-t-2 border-r-2 border-primary" />
      <span className="pointer-events-none absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-primary" />
      <span className="pointer-events-none absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-primary" />
      <div className="relative rounded-md border border-border/60 bg-background/50 p-3 md:p-5">{children}</div>
    </div>
  );
}
